import type {
  EditorImmerGetState,
  EditorImmerSetState,
} from './editor-store-types';

export interface ModeSlice {
  // Workflow mode state
  editorMode: 'visualization' | 'workflow' | 'data';
  workflowId: string | null;
  workflowName: string | null;

  // Workflow action bridge (for EditorLayout header integration)
  workflowActions: {
    handleSave: () => Promise<boolean>;
    handleExecute: () => Promise<void>;
    handlePublishToggle: (shouldPublish: boolean) => Promise<void>;
    openEnvironmentsDialog: () => void;
    workflowStatus: 'draft' | 'published' | null;
    selectedTarget: string;
    setSelectedTarget: (target: string) => void;
    isSaving: boolean;
    error: string | null;
  } | null;

  // Data mode state
  dataSourceId: string | null;
  dataModeName: string | null;

  // Data action bridge (for EditorLayout header integration)
  // Only stable callbacks — no reactive data to avoid render loops
  dataActions: {
    handleExecuteQuery: (query: string) => void;
    setActiveTab: (tab: string) => void;
  } | null;

  enterWorkflowMode: (workflowId: string, workflowName?: string) => void;
  exitWorkflowMode: () => void;
  registerWorkflowActions: (
    actions: NonNullable<ModeSlice['workflowActions']>,
  ) => void;
  clearWorkflowActions: () => void;
  enterDataMode: (dataSourceId?: string, name?: string) => void;
  exitDataMode: () => void;
  registerDataActions: (actions: NonNullable<ModeSlice['dataActions']>) => void;
  clearDataActions: () => void;
}

export const modeInitialState: Pick<
  ModeSlice,
  | 'editorMode'
  | 'workflowId'
  | 'workflowName'
  | 'workflowActions'
  | 'dataSourceId'
  | 'dataModeName'
  | 'dataActions'
> = {
  // Workflow mode initial state
  editorMode: 'visualization',
  workflowId: null,
  workflowName: null,
  workflowActions: null,

  // Data mode initial state
  dataSourceId: null,
  dataModeName: null,
  dataActions: null,
};

export function createModeSlice<TState extends ModeSlice>(
  set: EditorImmerSetState<TState>,
  _get: EditorImmerGetState<TState>,
): ModeSlice {
  return {
    ...modeInitialState,

    // Workflow mode actions
    enterWorkflowMode: (workflowId, workflowName) => {
      set((state) => {
        state.editorMode = 'workflow';
        state.workflowId = workflowId;
        state.workflowName = workflowName || null;
      });
    },

    exitWorkflowMode: () => {
      set((state) => {
        state.editorMode = 'visualization';
        state.workflowId = null;
        state.workflowName = null;
        state.workflowActions = null;
      });
    },

    registerWorkflowActions: (actions) => {
      set((state) => {
        state.workflowActions = actions;
      });
    },

    clearWorkflowActions: () => {
      set((state) => {
        state.workflowActions = null;
      });
    },

    // Data mode actions
    enterDataMode: (dataSourceId, dataModeName) => {
      set((state) => {
        state.editorMode = 'data';
        state.dataSourceId = dataSourceId || null;
        state.dataModeName = dataModeName || null;
      });
    },

    exitDataMode: () => {
      set((state) => {
        state.editorMode = 'visualization';
        state.dataSourceId = null;
        state.dataModeName = null;
        state.dataActions = null;
      });
    },

    registerDataActions: (actions) => {
      set((state) => {
        state.dataActions = actions;
      });
    },

    clearDataActions: () => {
      set((state) => {
        state.dataActions = null;
      });
    },
  };
}
